import { Button } from '@kalos/ui';
import { type ReactNode } from 'react';

import { DialogOrSheet } from './DialogOrSheet';

type FieldConfirmSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The question, naming the object: "Delete this payment?" */
  title: string;
  /** One line of consequence: what happens, and whether it can be undone. */
  description?: ReactNode;
  /** Names the action and object: "Delete Payment", never "OK" or "Yes". */
  confirmLabel: string;
  onConfirm: () => void;
  destructive?: boolean;
  /** While true (the action is running), it can't be closed and the button shows progress. */
  isBusy?: boolean;
  confirmDisabled?: boolean;
  cancelLabel?: string;
  /** Anything the confirmation needs beyond its one line (a reason field, a choice). */
  children?: ReactNode;
};

/**
 * A confirmation on a phone or tablet: a bottom sheet with the question as its title, one line of
 * consequence, the primary (or destructive) action and a ghost Cancel. Field layout only; desktop
 * keeps its AlertDialog.
 */
export const FieldConfirmSheet = ({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  onConfirm,
  destructive = false,
  isBusy = false,
  confirmDisabled = false,
  cancelLabel = 'Cancel',
  children,
}: FieldConfirmSheetProps) => (
  <DialogOrSheet open={open} onOpenChange={onOpenChange} title={title} isBusy={isBusy}>
    <div className="space-y-4">
      {description && <div className="text-muted-foreground text-sm">{description}</div>}
      {children}
      <div className="flex flex-col gap-2">
        <Button
          type="button"
          variant={destructive ? 'destructive' : 'default'}
          className="w-full"
          disabled={isBusy || confirmDisabled}
          isLoading={isBusy}
          onClick={onConfirm}
        >
          {confirmLabel}
        </Button>
        <Button
          type="button"
          variant="ghost"
          className="w-full"
          disabled={isBusy}
          onClick={() => onOpenChange(false)}
        >
          {cancelLabel}
        </Button>
      </div>
    </div>
  </DialogOrSheet>
);
